import React, { ReactNode, useState } from 'react';
import { InstallBugherd } from './InstallBugherd/index';
import General from '../project_settings/forms/general/general';
import Security from '../project_settings/forms/security/security';
import IntegrationSettings from '../integration_settings';
import GuestPermissions from '../guest_permissions';
import Sidebar from '../project_settings/forms/sidebar';
import EditTags from '../project_settings/forms/tags';
import Advanced from '../project_settings/forms/advanced/advanced';
import CommitHooks from './commitHooks';
import DisableProject from './disableProject';
import * as translations from './strings';
import { getLangKey } from 'appJS/models/Language';
import { GetItemArgs, TabItems } from './types';
import styles from './index.module.css';
import cx from 'classnames';
import ExportBugs from './exportBugs';
import integrationsBanner from '../integration_settings/images/integrations-banner.png';
import {
  HelpGuideCard,
  HelpGuideCardProps
} from 'appJS/components/HelpGuideCard';
import { helpGuides } from './helpGuides';
import { AISettings } from './AiSettings';

const strings = translations[getLangKey()];

export const getMobileItems = (handleMobileClick: (key: string) => void) => {
  const mobileLabel = ({ label, key }) => (
    <span onClick={() => handleMobileClick(key)}>{label}</span>
  );
  const items = [
    {
      key: 'general',
      label: strings.generalTitle
    },
    {
      key: 'guest_permissions',
      label: strings.guestPermissions
    },
    {
      key: 'install_bugherd',
      label: strings.installBugherd
    },
    {
      key: 'integrations',
      label: strings.integrationsTitle
    },
    {
      key: 'sidebar',
      label: strings.feedbackSettings
    },
    {
      key: 'security',
      label: strings.securityTitle
    },
    {
      key: 'bugherd-ai',
      label: strings.aiSettings
    },
    {
      key: 'tags',
      label: strings.tags
    },
    {
      key: 'commit_hook',
      label: strings.commitHook
    },
    {
      key: 'export_bugs',
      label: strings.exportBugs
    },
    {
      key: 'disable_/_delete_project',
      label: strings.disableDelete
    },
    {
      key: 'advanced',
      label: strings.advanced
    }
  ];

  return items.map(({ key, label }) => ({
    key,
    label: mobileLabel({ label, key })
  }));
};

const itemChild = ({
  children,
  title,
  key,
  guideProps,
  collapsed,
  hiddenGuide
}: {
  children: ReactNode;
  title: ReactNode;
  key?: string;
  guideProps?: HelpGuideCardProps;
  collapsed?: boolean;
  hiddenGuide?: boolean;
}): ReactNode => (
  <div
    className={cx(styles.settingsFormWrapper, {
      [styles.guestPermissionsView]: key === 'guest_permissions',
      [styles.aiSettingsView]: key === 'bugherd-ai',
      [styles.collapsedView]: !!collapsed,
      [styles.aiGuideHidden]: key === 'bugherd-ai' && hiddenGuide
    })}
  >
    <div className={styles.settingsFormWrapperInner}>
      {typeof title === 'string' ? (
        <h2 className={styles.settingsTitle}>
          {title}
        </h2>
      ) : (
        title
      )}
      {children}
    </div>
    {guideProps && <HelpGuideCard {...guideProps} />}
  </div>
);

export const useGetTabItems = ({
  project,
  accessTo: {
    guestsSeeGuestsAccess,
    guestsEditTaskStatusAllowed,
    mobileScreenshots,
    billing
  },
  bugherdUrl,
  integrations,
  user,
  bitBucketHook,
  gitHubHook,
  gitLabHook,
  refetch,
  projectInit,
  authenticityToken,
  showAssetsPage,
  currentUser,
  collapsed,
  container
}: TabItems) => {
  const [aiGuideHidden, setAIGuideHidden] = useState(false);
  const {
    integrations: integrationsGuide,
    clientPermissions: clientPermissionsGuide,
    install: installGuide,
    aiSettings: aiSettingsGuide
  } = helpGuides({ aiSettings: { onHideGuide: () => setAIGuideHidden(true) } });

  const createTagsViaNewTask = project?.createTagsViaNewTask;
  const isAdmin = user?.admin;

  const items: GetItemArgs[] = [
    {
      label: strings.generalTitle,
      key: 'general',
      children: itemChild({
        title: strings.generalTitle,
        children: (
          <General
            project={project}
            refetch={refetch}
            showAssetsPage={showAssetsPage}
          />
        )
      })
    },
    {
      label: strings.guestPermissions,
      key: 'guest_permissions',
      children: itemChild({
        title: strings.guestPermissions,
        children: (
          <GuestPermissions
            project={project}
            guestsSeeGuestsAccess={guestsSeeGuestsAccess}
            guestsEditTaskStatusAllowed={guestsEditTaskStatusAllowed}
            billing={billing}
            container={container}
          />
        ),
        key: 'guest_permissions',
        guideProps: clientPermissionsGuide
      })
    },
    {
      label: strings.installBugherd,
      key: 'install_bugherd',
      children: itemChild({
        title: strings.installBugherdTitle,
        children: (
          <InstallBugherd
            project={project}
            bugherdUrl={bugherdUrl}
            user={user}
            onInstallCallback={refetch}
          />
        ),
        key: 'install_bugherd',
        guideProps: installGuide
      })
    },
    {
      label: strings.integrationsTitle,
      key: 'integrations',
      children: itemChild({
        title: (
          <div className={styles.integrationsBanner}>
            <div className={styles.integrationsHeader}>
              <h1>{strings.integrationsTitle}</h1>
              <p className={styles.integrationsDescription}>
                {strings.integrationsDescription}
              </p>
            </div>
            <img
              className={styles.bannerImage}
              src={integrationsBanner}
              alt="integrations_banner"
            />
          </div>
        ),
        children: (
          <IntegrationSettings
            {...integrations}
            currentUser={currentUser}
          />
        ),
        guideProps: integrationsGuide
      })
    },
    {
      label: strings.feedbackSettings,
      key: 'sidebar',
      children: itemChild({
        title: strings.sidebarSettingsTitle,
        children: <Sidebar project={project} billing={billing} />
      })
    },
    {
      label: strings.securityTitle,
      key: 'security',
      children: itemChild({
        title: strings.securityTitle,
        children: (
          <Security
            project={project}
            accessTo={{
              mobileScreenshots
            }}
            refetch={refetch}
          />
        )
      })
    },
    {
      label: strings.aiSettings,
      key: 'bugherd-ai',
      children: itemChild({
        title: null,
        key: 'bugherd-ai',
        children: (
          <AISettings
            project={project}
            container={container}
            refetch={refetch}
          />
        ),
        guideProps: aiSettingsGuide,
        collapsed,
        hiddenGuide: aiGuideHidden
      })
    },
    {
      label: strings.tags,
      key: 'tags',
      children: itemChild({
        title: strings.editTagsTitle,
        children: (
          <EditTags
            projectId={project?.id}
            createTagsViaNewTask={createTagsViaNewTask}
          />
        )
      })
    },
    {
      label: strings.commitHook,
      key: 'commit_hook',
      children: itemChild({
        title: strings.commitTitle,
        children: (
          <CommitHooks
            projectId={project?.id}
            bitBucketHook={bitBucketHook}
            gitHubHook={gitHubHook}
            gitLabHook={gitLabHook}
            organizationId={project?.organizationId}
          />
        )
      })
    },
    {
      label: strings.exportBugs,
      key: 'export_bugs',
      children: itemChild({
        title: strings.exportBugs,
        children: (
          <ExportBugs
            email={user?.email}
            authenticityToken={authenticityToken}
            projectId={project?.id}
            organizationId={project?.organizationId}
          />
        )
      })
    },
    {
      label: strings.disableDelete,
      key: 'disable_delete_project',
      children: itemChild({
        title: strings.disableTitle,
        children: <DisableProject project={projectInit} />
      })
    },
    {
      label: strings.advanced,
      key: 'advanced',
      children: itemChild({
        title: strings.advancedTitle,
        children: <Advanced project={project} refetch={refetch} />
      })
    }
  ];

  if (isAdmin) {
    items.push({
      label: (
        <a target="_blank" href={`/test/widget?apikey=${project?.apiKey}`}>
          Test project sidebar
        </a>
      ),
      key: 'test_project_sidebar'
    });
  }

  return items;
};
